import OctApiAdaptor from '@/components/api-adaptor/OctApiAdaptor'

const props = {
  /** Id */
  id: {
    type: String,
    default: ''
  },
  /** Artwork type */
  type: {
    type: String,
    default: 'physical'
  },
  /** Artwork title */
  title: {
    type: String,
    default: ''
  },
  /** Artwork title in English */
  titleEN: {
    type: String,
    default: ''
  },
  /** Published Date */
  publishedAt: {
    type: String,
    default: ''
  },
  /** Materials */
  materials: {
    type: Array,
    default: () => []
  },
  /** Artwork images */
  images: {
    type: Array,
    default: () => []
  },
  /** Whether the artwork is unique */
  unique: {
    type: Boolean,
    default: true
  },
  /** Number of editions */
  edition: {
    type: Number,
    default: 0
  },
  /** Price */
  price: {
    type: Number,
    default: 0
  },
  /** Note */
  note: {
    type: String,
    default: ''
  },
  /** Note in English */
  noteEN: {
    type: String,
    default: ''
  },
  /** Creators of the artwork */
  artists: {
    type: Array,
    default: () => []
  },
  /** Exbition list the artwork */
  exhibitions: {
    type: Array,
    default: () => []
  },
  /** Size */
  size: {
    type: Object,
    default () {
      return {
        width: '',
        height: '',
        depth: ''
      }
    }
  },
  /** Tags */
  tags: {
    type: Array,
    default: () => [] 
  },
  /** Extra data */
  data: {
    type: [String,Object],
    default: () => {
      return {
        url: '',
        sizes: [
          {
            width: '',
            height: '',
            depth: ''
          },
          {
            width: '',
            height: '',
            depth: ''
          }
        ],
        /**
         * A gallery where you can edit the artwork
         */
        extraGalleries: []
      }
    }
  },
  /** Public staus */
  status: {
    type: String,
    default: 'private'
  }
}

export const createProps = () => {
  return Object.keys(props).reduce((acc, cur) => {
    acc[cur] = typeof props[cur].default === 'function' ?
      props[cur].default() : props[cur].default
    return acc
  }, {})
}

const sanitize = (val) => val ? `"${val.replace(/"/g, '""')}"` : '""'

export const getCSV = (item) => {
  // CSV columns
  const columns = []

  // Append data
  Array(
    'type',
    'titleEN',
    'title',
    'publishedAt',
    'materials',
    'size',
    'edition',
    'note',
    'noteEN',
    'tags',
    'artists'
  ).forEach(val => {
    if (!item[val]) // Invalid
      columns.push('')
    else if (val === 'artists') // Artists
      columns.push(sanitize(
        item[val].map(i => i.tradeNameEN || i.tradeName).join(',')
      ))
    else if (Array.isArray(item[val])) // Array
      columns.push(sanitize(item[val].join(',')))
    else if (typeof item[val] === 'object') // Object
      columns.push(sanitize(JSON.stringify(item[val]).
        replace(/[{}"]/g, '')))
    else if (item[val].toString) // Has toString function
      columns.push(sanitize(item[val].toString()))
    else // Others
      columns.push(sanitize(item[val]))
  })

  return columns.join(',')
}

export default {
  name: 'octArtwork',
  props: props,
  data: () => ({
    /** List of materials */
    materialList: [
      {
        text: 'None',
        value: '',
      }
    ]
  }),
  computed: {
    /** Id */
    $_octArtwork_id: {
      get () {
        return this.id
      },
      set (val) {
        this.$emit('update:id', val)
      }
    },
    /** Artwork type */
    $_octArtwork_type: {
      get () {
        return this.type
      },
      set (val) {
        this.$emit('update:type', val)
      }
    },
    /** Artwork title */
    $_octArtwork_title: {
      get () {
        return this.title
      },
      set (val) {
        this.$emit('update:title', val)
      }
    },
    /** Artwork title in English */
    $_octArtwork_titleEN: {
      get () {
        return this.titleEN
      },
      set (val) {
        this.$emit('update:titleEN', val)
      }
    },
    /** Published Date */
    $_octArtwork_publishedAt: {
      get () {
        return this.publishedAt
      },
      set (val) {
        this.$emit('update:publishedAt', val)
      }
    },
    /** Materials */
    $_octArtwork_materials: {
      get () {
        return this.materials
      },
      set (val) {
        this.$emit('update:materials', val)
      }
    },
    /** Artwork images */
    $_octArtwork_images: {
      get () {
        return this.images
      },
      set (val) {
        this.$emit('update:images', val)
      }
    },
    /** Whether the artwork is unique */
    $_octArtwork_unique: {
      get () {
        return this.unique
      },
      set (val) {
        this.$emit('update:unique', val)
      }
    },
    /** Number of editions */
    $_octArtwork_edition: {
      get () {
        return this.edition
      },
      set (val) {
        this.$emit('update:edition', Number(val))
      }
    },
    /** Price */
    $_octArtwork_price: {
      get () {
        return this.price
      },
      set (val) {
        this.$emit('update:price', val)
      }
    },
    /** Note */
    $_octArtwork_note: {
      get () {
        return this.note
      },
      set (val) {
        this.$emit('update:note', val)
      }
    },
    /** Note in English */
    $_octArtwork_noteEN: {
      get () {
        return this.noteEN
      },
      set (val) {
        this.$emit('update:noteEN', val)
      }
    },
    /** Creators of the artwork */
    $_octArtwork_artists: {
      get () {
        return this.artists
      },
      set (val) {
        this.$emit('update:artists', val)
      }
    },
    /** Exbition list the artwork */
    $_octArtwork_exhibitions: {
      get () {
        return this.exhibitions
      },
      set (val) {
        this.$emit('update:exhibitions', val)
      }
    },
    /** Size */
    $_octArtwork_size: {
      get () {
        return this.size
      },
      set (val) {
        this.$emit('update:size', val)
      }
    },
    /** Tags */
    $_octArtwork_tags: {
      get () {
        return this.tags
      },
      set (val) {
        this.$emit('update:tags', val)
      }
    },
    /** Extra data */
    $_octArtwork_data: {
      get () {
        return this.data
      },
      set (val) {
        this.$emit('update:data', val)
      }
    },
    /** Public staus */
    $_octArtwork_status: {
      get () {
        return this.status === 'private'
      },
      set (val) {
        this.$emit('update:status', val ? 'private' : 'public')
      }
    }
  },
  created () {
    // Fetch all materials
    OctApiAdaptor.getMaterials()
      .then(response => this.materialList.push(
        ...response.sort((a, b) => a.text.toUpperCase() < b.text.toUpperCase() ? -1 : 1)
      ))
      .catch(() => alert('素材が取得できませんでした。しばらく経ってからもう一度やり直してください。'))
  }
}
